import { apiErrorMessage, customFetch, isForbidden } from '@agos/api-client'
import {
  getGetOrdersQueryKey,
  postOrdersOrderIdConfirmStatus,
  useGetOrders,
  usePatchOrdersOrderId,
} from '@agos/api-client/orders'
import type { OrderListItem, PublicGeneralSettings } from '@agos/api-client/model'
import {
  T,
  credentialsNeeded,
  formatMoney,
  formatWeekdayTime,
  protectedStatusPlan,
  statusRoleNote,
  tt,
  useAuth,
} from '@agos/shared'
import {
  CancelOrderModal,
  DataTable,
  FilterBar,
  PageHeader,
  StatusBadge,
  TabGroup,
  cn,
  toast,
  type Column, BUTTON_PRIMARY} from '@agos/ui'
import { useQueryClient } from '@tanstack/react-query'
import { CalendarClock, Check, CheckCircle2, ChevronRight, MapPin, Package, Pencil, Phone, Plus, ShoppingBag, StickyNote, Store, Truck, User, X } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { useLocation } from 'wouter'


import {
  ORDER_TYPE_LABELS,
  isOrderClosed,
  orderStatusColor,
  orderStatusLabel,
  paymentMethodLabel,
} from '../order-status'
import { mayOpenOrderEditor } from '../order-rights'
import {
  preorderCloseReason,
  preorderUrgency,
  timeUntilScheduled,
  URGENCY_CLASSES,
} from './urgency'

/**
 * Ported from legacy `pages/preorders.tsx`. Spec:
 * `docs/pages/manager-orders/preorders.md`.
 *
 * §14.1: the board polls every **30 seconds**. That is not a preference —
 * a longer interval means a pre-order comes due without the kitchen noticing.
 * §14.8: everything here sorts and counts by `scheduledAt`, never `createdAt`.
 */

/** §14.1 — copied exactly from legacy. */
const POLL_INTERVAL_MS = 30_000
/** Legacy asks for everything and filters client-side. The API types every
 *  query parameter as a string, so it is sent as one. */
const PAGE_SIZE = '500'
const DEFAULT_UPCOMING_THRESHOLD_MINUTES = 60

const TYPE_ICONS: Record<string, typeof Truck> = {
  delivery: Truck,
  restaurant: Store,
  bar: ShoppingBag,
}

export function PreordersPage() {
  const queryClient = useQueryClient()
  const [, navigate] = useLocation()
  // §9.653 - the cancel asks a permission, not a role, so the prompt needs the reader too.
  const { user, hasPermission } = useAuth()
  /* §9.744, owner O114 - *Upraviť* opens the order in the editor, whose save asks `edit_order` (BACKEND.md
     §10A.397) and whose route asks `new_order` (§9.744 amended). Read once, so every row answers the same question. */
  const mayEditOrder = mayOpenOrderEditor(hasPermission)

  const [statusFilter, setStatusFilter] = useState('active')
  const [search, setSearch] = useState('')
  const [cancelling, setCancelling] = useState<OrderListItem | null>(null)
  const [upcomingThresholdMinutes, setUpcomingThresholdMinutes] = useState(
    DEFAULT_UPCOMING_THRESHOLD_MINUTES,
  )

  const {
    data: orderList,
    isLoading,
    dataUpdatedAt,
  } = useGetOrders(
    { all_time: 'true', page_size: PAGE_SIZE },
    { query: { refetchInterval: POLL_INTERVAL_MS } },
  )
  const updateOrder = usePatchOrdersOrderId()

  // The "coming up" threshold is the kitchen display interval from settings.
  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const settings = await customFetch<Partial<PublicGeneralSettings> | null>(
          '/api/v1/settings/general',
        )
        if (!cancelled && settings?.kitchenDisplayInterval != null) {
          setUpcomingThresholdMinutes(Number(settings.kitchenDisplayInterval))
        }
      } catch {
        // The default of an hour is a reasonable answer.
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  const allOrders = useMemo(() => orderList?.data ?? [], [orderList])

  const preorders = useMemo(() => {
    let list = allOrders.filter((order) => order.isPreorder)

    const query = search.trim().toLowerCase()
    if (query) {
      list = list.filter(
        (order) =>
          (order.customerName ?? '').toLowerCase().includes(query) ||
          String(order.orderNumber ?? '')
            .toLowerCase()
            .includes(query),
      )
    }

    const bySchedule = (left: OrderListItem, right: OrderListItem) =>
      new Date(left.scheduledAt ?? 0).getTime() - new Date(right.scheduledAt ?? 0).getTime()

    if (statusFilter === 'active') {
      return list.filter((order) => !isOrderClosed(order.status)).sort(bySchedule)
    }
    if (statusFilter === 'done') {
      return list.filter((order) => isOrderClosed(order.status)).sort((left, right) => bySchedule(right, left))
    }
    return [...list].sort(bySchedule)
  }, [allOrders, statusFilter, search])

  /**
   * The countdown and the "coming up" count are measured from when the data
   * arrived, not from the clock at render. That keeps them pure, and it is also
   * the more truthful reading: a board showing a 30-second-old list should say
   * what was true 30 seconds ago, not mix fresh arithmetic with stale rows.
   */
  const now = dataUpdatedAt || 0

  const upcomingCount = useMemo(() => {
    const deadline = now + upcomingThresholdMinutes * 60_000
    return allOrders.filter(
      (order) =>
        order.isPreorder &&
        !isOrderClosed(order.status) &&
        new Date(order.scheduledAt ?? 0).getTime() < deadline,
    ).length
  }, [allOrders, upcomingThresholdMinutes, now])

  const refreshOrders = () => queryClient.invalidateQueries({ queryKey: getGetOrdersQueryKey() })

  /**
   * §9.343 - the three settling statuses go to `confirm-status`; everything else is an ordinary
   * `PATCH`.
   *
   * **This screen already knew the rule for `cancelled`** - `cancelWithCredentials` below - and sent
   * `delivered` through the plain `PATCH`, which `OrderUpdateService.guard` refuses. Its `onError`
   * then printed the server's *"Použite /confirm-status"* to the operator, which is the owner's own
   * report on a second screen.
   */
  const changeStatus = (orderId: number, status: string) => {
    if (protectedStatusPlan(status) === 'confirm') {
      void (async () => {
        try {
          await postOrdersOrderIdConfirmStatus(orderId, {
            newStatus: status,
            reason: preorderCloseReason(),
          })
          void refreshOrders()
          toast({ title: tt('Stav zmenený') })
        } catch (caught) {
          toast({
            variant: 'destructive',
            title: tt('Chyba'),
            // A role refusal says who may, rather than repeating a sentence written for a developer.
            description: isForbidden(caught)
              ? statusRoleNote(status)
              : apiErrorMessage(caught, tt('Nepodarilo sa zmeniť stav.')),
          })
        }
      })()
      return
    }
    return updateOrder.mutate(
      { orderId, data: { status } },
      {
        onSuccess: () => {
          void refreshOrders()
          toast({ title: tt('Stav zmenený') })
        },
        onError: (caught) =>
          toast({
            variant: 'destructive',
            title: tt('Chyba'),
            description: apiErrorMessage(caught, tt('Nepodarilo sa zmeniť stav.')),
          }),
      },
    )
  }

  /**
   * Cancelling goes through `confirm-status`, which re-authenticates the person
   * doing it and records the reason. It is not the same call as a status change.
   */
  const cancelWithCredentials = async (email: string, password: string, reason: string) => {
    if (cancelling?.id === undefined) return
    await customFetch<unknown>(`/api/v1/orders/${cancelling.id}/confirm-status`, {
      method: 'POST',
      // §9.344 - sent only when the dialog collected them. **Blank is worse than absent**:
      // `OrderActorResolver.resolve` tests `email != null && password != null`, so an empty pair
      // falls to `byCredentials` and fails on a password nobody typed.
      body: JSON.stringify({
        newStatus: 'cancelled',
        reason,
        ...(email.trim() === '' ? {} : { email: email.trim(), password }),
      }),
    })
    toast({ title: tt('Predobjednávka stornovaná'), description: `${tt('Dôvod:')} ${reason}` })
    setCancelling(null)
    void refreshOrders()
  }


  const columns: Column<OrderListItem>[] = [
    {
      key: 'orderNumber',
      header: '#',
      sortable: true,
      sortValue: (row) => row.orderNumber ?? '',
      render: (row) => (
        <span className="font-mono text-xs font-bold text-primary">#{row.orderNumber}</span>
      ),
    },
    {
      key: 'status',
      header: tt('Stav'),
      sortable: true,
      sortValue: (row) => row.status ?? '',
      render: (row) => (
        <StatusBadge label={orderStatusLabel(row.status)} color={orderStatusColor(row.status)} />
      ),
    },
    {
      key: 'scheduledAt',
      header: tt('Plánované'),
      sortable: true,
      sortValue: (row) => new Date(row.scheduledAt ?? 0).getTime(),
      render: (row) => {
        if (!row.scheduledAt) return <span className="text-muted-foreground text-xs">-</span>
        const urgency = preorderUrgency(row.scheduledAt, now)
        return (
          <div className="space-y-0.5">
            <div className="flex items-center gap-1.5">
              <CalendarClock className="w-3.5 h-3.5 text-primary flex-shrink-0" />
              <span className="font-bold text-foreground text-xs">
                {formatWeekdayTime(row.scheduledAt)}
              </span>
            </div>
            {!isOrderClosed(row.status) && (
              <span
                className={cn(
                  'text-[10px] font-bold px-1.5 py-0.5 rounded-md inline-block',
                  URGENCY_CLASSES[urgency],
                )}
              >
                {timeUntilScheduled(row.scheduledAt, now)}
              </span>
            )}
          </div>
        )
      },
    },
    {
      key: 'type',
      header: tt('Typ'),
      sortable: true,
      sortValue: (row) => row.type ?? '',
      render: (row) => {
        const TypeIcon = TYPE_ICONS[row.type ?? ''] ?? Truck
        return (
          <span className="flex items-center gap-1 text-xs text-muted-foreground">
            <TypeIcon className="w-3 h-3" />
            {ORDER_TYPE_LABELS[row.type ?? ''] ?? row.type}
          </span>
        )
      },
    },
    {
      key: 'customer',
      header: tt('Zákazník'),
      sortable: true,
      sortValue: (row) => row.customerName ?? '',
      render: (row) => (
        <div className="space-y-0.5">
          {row.customerName && (
            <span className="flex items-center gap-1 font-semibold text-foreground text-xs">
              <User className="w-3 h-3 text-muted-foreground" /> {row.customerName}
            </span>
          )}
          {row.customerPhone && (
            <a
              href={`tel:${row.customerPhone}`}
              // §9.381 - as on the driving surface and the dispatch board.
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-1 text-blue-500 text-[11px]"
            >
              <Phone className="w-3 h-3" /> {row.customerPhone}
            </a>
          )}
          {row.customerAddress && (
            <span className="flex items-center gap-1 text-muted-foreground text-[11px]">
              <MapPin className="w-3 h-3" /> {row.customerAddress}
            </span>
          )}
        </div>
      ),
    },
    {
      key: 'items',
      header: tt('Položky'),
      sortable: true,
      sortValue: (row) => row.items?.length ?? 0,
      render: (row) => (
        <div className="text-xs text-muted-foreground max-w-[200px]">
          <div className="flex items-center gap-1 truncate">
            <Package className="w-3 h-3 flex-shrink-0" />
            <span className="truncate">
              {row.items?.map((item) => `${item.quantity}x ${item.productName}`).join(' · ')}
            </span>
          </div>
          {/* §9.428 - **the same defect §9.425 fixed on the dispatch board, on its sibling screen.**
            * Found by sweeping for free text under `truncate` rather than by another report: a
            * preorder's note is an instruction somebody has to act on, and it was 10 px and clipped.
            * `break-words` because a row that grows is the right trade for the one field whose
            * purpose is that it gets read. The `title` keeps the hover. */}
          {row.notes && (
            <div
              className="flex items-start gap-1 text-xs bg-amber-500/10 border border-amber-500/30 rounded px-1.5 py-0.5 text-amber-700 dark:text-amber-400 mt-1"
              title={row.notes}
              data-preorder-note
            >
              <StickyNote className="w-3 h-3 shrink-0 mt-0.5" />
              <span className="min-w-0 break-words">{row.notes}</span>
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'total',
      header: tt('Suma'),
      sortable: true,
      sortValue: (row) => row.total ?? 0,
      className: 'text-right',
      headerClassName: 'text-right',
      render: (row) => (
        <div className="text-right space-y-0.5">
          <div className="font-bold text-foreground text-sm">{formatMoney(row.total)}</div>
          <div className="text-[10px] text-muted-foreground">
            {paymentMethodLabel(row.paymentMethod)}
          </div>
        </div>
      ),
    },
    {
      key: 'actions',
      header: '',
      render: (row) => {
        if (isOrderClosed(row.status) || row.id === undefined) return null
        return (
          <div className="flex flex-wrap gap-1 justify-end">
            {/* §9.744 - only for somebody holding `edit_order` and `new_order`: the editor it opens cannot be opened
              * without the second or save without the first. */}
            {mayEditOrder && (
              <button
                type="button"
                data-preorder-edit={row.id}
                onClick={() => navigate(`/new-order?edit=${row.id}`)}
                className="flex items-center gap-1 px-2 py-1.5 rounded-lg bg-muted text-foreground font-bold text-[11px] hover:bg-primary/10 hover:text-primary transition-colors border border-border"
              >
                <Pencil className="w-3 h-3" /> <T>Upraviť</T>
              </button>
            )}
            {row.status === 'new' && (
              <button
                type="button"
                onClick={() => changeStatus(row.id as number, 'confirmed')}
                data-preorder-next="confirmed"
                className="flex items-center gap-1 px-2 py-1.5 rounded-lg bg-cyan-500 text-white font-bold text-[11px] hover:bg-cyan-600 transition-colors"
              >
                <Check className="w-3 h-3" /> <T>Potvrdiť</T>
              </button>
            )}
            {(row.status === 'new' || row.status === 'confirmed') && (
              <button
                type="button"
                onClick={() => changeStatus(row.id as number, 'preparing')}
                data-preorder-next="preparing"
                className="flex items-center gap-1 px-2 py-1.5 rounded-lg bg-amber-500 text-white font-bold text-[11px] hover:bg-amber-600 transition-colors"
              >
                <ChevronRight className="w-3 h-3" /> <T>Kuchyňa</T>
              </button>
            )}
            {row.status === 'preparing' && (
              <button
                type="button"
                onClick={() => changeStatus(row.id as number, 'ready')}
                data-preorder-next="ready"
                className="flex items-center gap-1 px-2 py-1.5 rounded-lg bg-orange-500 text-white font-bold text-[11px] hover:bg-orange-600 transition-colors"
              >
                <Check className="w-3 h-3" /> <T>Hotová</T>
              </button>
            )}
            {row.status === 'ready' && (
              <button
                type="button"
                onClick={() => changeStatus(row.id as number, 'delivered')}
                data-preorder-next="delivered"
                className="flex items-center gap-1 px-2 py-1.5 rounded-lg bg-emerald-500 text-white font-bold text-[11px] hover:bg-emerald-600 transition-colors"
              >
                <CheckCircle2 className="w-3 h-3" /> <T>Doručená</T>
              </button>
            )}
            <button
              type="button"
              onClick={() => setCancelling(row)}
              className="flex items-center gap-1 px-2 py-1.5 rounded-lg bg-muted text-muted-foreground font-bold text-[11px] hover:bg-red-500/10 hover:text-red-500 transition-colors"
            >
              <X className="w-3 h-3" /> <T>Zrušiť</T>
            </button>
          </div>
        )
      },
    },
  ]

  return (
    <div className="space-y-5">
      <PageHeader
        title={tt('Predobjednávky')}
        icon={<CalendarClock className="w-5 h-5" />}
        description={
          upcomingCount > 0
            ? `${upcomingCount} ${tt('blíži sa do')} ${upcomingThresholdMinutes} ${tt('min')}`
            : undefined
        }
        actions={
          <button
            type="button"
            onClick={() => navigate('/new-order?casenka=1')}
            className={cn('flex items-center gap-1.5 px-3 py-2 rounded-xl', BUTTON_PRIMARY, 'text-sm')}
          >
            <Plus className="w-4 h-4" /> <T>Nová predobjednávka</T>
          </button>
        }
      />

      <TabGroup
        tabs={[
          { id: 'active', label: tt('Aktívne') },
          { id: 'done', label: tt('Vybavené') },
          { id: 'all', label: tt('Všetky') },
        ]}
        active={statusFilter}
        onChange={setStatusFilter}
      />

      <FilterBar
        search={{
          value: search,
          onChange: setSearch,
          placeholder: tt('Hľadať meno / číslo objednávky...'),
        }}
      />

      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((placeholder) => (
            <div
              key={placeholder}
              className="h-32 rounded-2xl bg-card border border-border animate-pulse"
            />
          ))}
        </div>
      ) : (
        <DataTable
          columns={columns}
          data={preorders}
          rowKey={(row) => row.id ?? 0}
          pageSize={20}
          compact
          emptyMessage={tt('Žiadne predobjednávky')}
          emptyIcon={<CalendarClock className="w-14 h-14 mb-3 opacity-30" />}
        />
      )}

      {cancelling && (
        <CancelOrderModal
          order={cancelling}
          askCredentials={credentialsNeeded(user?.systemRole ?? '', 'cancelled', hasPermission)}
          // §9.344 - the reason is always asked; the credentials only when this session cannot
          // authorise the change on its own.
          onClose={() => setCancelling(null)}
          onConfirm={cancelWithCredentials}
        />
      )}
    </div>
  )
}
